import React, { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import SuccessToast from 'components/application/SuccessToast';

import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import DuplicationInProgressModal from 'components/projectsDashboard/components/DuplicationInProgressModal';
import DuplicationWithOutOfDateActivitiesModal from 'components/projectsDashboard/components/DuplicationWithOutOfDateActivitiesModal';
import RequestDuplicationModal from 'components/projectsDashboard/components/RequestDuplicationModal';

import { formatData } from 'components/helpers/reactQuery';

export default function ProjectDuplicationManager({
  isModalOpen,
  onCloseModalJourney,
  project,
}) {
  const breadBoard = useBreadBoard();
  const queryClient = useQueryClient();

  const { projectDuplicationPolicy } = useProjectsDashboardContext();

  const [hasConfirmedOutOfDateActivities, setHasConfirmedOutOfDateActivities] =
    useState(false);
  // Could be derived from duplication processes cache, but using state to ensure we're capturing the correct duplication journey
  const [activeDuplicationId, setActiveDuplicationId] = useState(null);

  const shouldCheckOutOfDateActivities =
    !!project &&
    !hasConfirmedOutOfDateActivities &&
    projectDuplicationPolicy.canCreate;

  const {
    data: domainOutOfDateActivities,
    isSuccess: isDomainOutOfDateActivitiesFetchSuccess,
  } = useQuery({
    enabled: shouldCheckOutOfDateActivities,
    queryKey: project ? ['projects', project.id, 'outOfDateActivities'] : [],
    queryFn: async () => {
      const response = await axios.get(
        `/projects/${project.id}/out_of_date_activities`,
      );
      return response.data;
    },
    staleTime: 300000, // Marking data as stale after 5 mins, as we need to have fairly recent data relating to any out of date activities
  });

  const { data: duplicationProcesses, isError } = useQuery({
    queryKey: ['duplicationProcesses'],
    queryFn: async () => {
      const response = await axios.get('/duplication_processes');

      return response.data;
    },
    placeholderData: { data: [], included: [] },
    select: (data) =>
      formatData({
        data,
        resourceTypes: ['originalProject', 'duplicatedProject'],
      }),
    refetchInterval: (data) => {
      // Prevents refetching when only finished processes exist
      const hasWorkingDuplication = data?.data?.some((process) =>
        ['working', 'queued'].includes(process?.attributes?.status),
      );
      return hasWorkingDuplication ? 1000 : false;
    },
    refetchIntervalInBackground: true,
  });
  const handleStartDuplication = ({ duplicationProcessId }) => {
    setActiveDuplicationId(duplicationProcessId);
  };

  const handleCloseModalJourney = () => {
    onCloseModalJourney();
    setHasConfirmedOutOfDateActivities(false);
    setActiveDuplicationId(null);
  };

  if (isError) return null;

  const statusForActiveDuplication = duplicationProcesses.data.find(
    (process) => process.id === activeDuplicationId,
  )?.attributes.status;

  const duplicationDisplayStatus = (() => {
    if (!isModalOpen || !isDomainOutOfDateActivitiesFetchSuccess)
      return 'initial';

    const hasOutOfDateActivities = domainOutOfDateActivities.data.length > 0;

    if (!hasConfirmedOutOfDateActivities && hasOutOfDateActivities) {
      return 'awaitingActivitiesConfirmation';
    }

    if (activeDuplicationId) {
      if (['working', 'queued'].includes(statusForActiveDuplication)) {
        return 'inProgress';
      }
      return 'awaitingSubmission';
    }

    return 'awaitingSubmission';
  })();

  const finishedDuplications = duplicationProcesses.data.filter((process) => {
    const { status } = process.attributes;

    return status === 'completed' || status === 'failed';
  });

  if (finishedDuplications.length > 0) {
    finishedDuplications.forEach((process) => {
      const { status } = process.attributes;

      (function displayDuplicationSuccess() {
        if (status === 'completed') {
          const duplicatedProject =
            duplicationProcesses.included.duplicatedProject[
              process.relationships.duplicatedProject.data.id
            ];

          breadBoard.addToast(
            <SuccessToast
              message={
                <>
                  <a href={`/projects/${duplicatedProject.id}`}>
                    {duplicatedProject.attributes.name}
                  </a>{' '}
                  created successfully
                </>
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        }
      })();

      (function displayDuplicationError() {
        if (status === 'failed') {
          const originalProjectName =
            duplicationProcesses.included.originalProject[
              process.relationships.originalProject.data.id
            ].attributes.name;

          breadBoard.addInedibleToast({
            fullMessage: `Could not duplicate ${originalProjectName}`,
          });
        }
      })();

      if (activeDuplicationId === process.id) {
        handleCloseModalJourney();
        setActiveDuplicationId(null);
      }
    });

    (function clearFinishedDuplicationsFromCache() {
      const finishedIds = new Set(
        finishedDuplications.map((process) => process.id),
      );

      const unfinishedDuplications = duplicationProcesses.data.filter(
        (process) => !finishedIds.has(process.id),
      );

      queryClient.setQueryData(['duplicationProcesses'], (prevData) => ({
        ...prevData,
        data: unfinishedDuplications,
        included: { ...duplicationProcesses.included.originalProject },
      }));
    })();

    queryClient.invalidateQueries(['projectReports']);
  }

  if (!project) return null;

  return {
    awaitingActivitiesConfirmation: (
      <DuplicationWithOutOfDateActivitiesModal
        onClose={handleCloseModalJourney}
        onConfirmDupWithOutOfDateActivities={() =>
          setHasConfirmedOutOfDateActivities(true)
        }
        originalProject={project}
        outOfDateActivities={domainOutOfDateActivities?.data}
      />
    ),
    awaitingSubmission: (
      <RequestDuplicationModal
        onClose={handleCloseModalJourney}
        onStartDuplication={handleStartDuplication}
        originalProject={project}
      />
    ),
    inProgress: (
      <DuplicationInProgressModal
        onClose={handleCloseModalJourney}
        originalProject={project}
      />
    ),
    initial: null,
  }[duplicationDisplayStatus];
}

ProjectDuplicationManager.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  onCloseModalJourney: PropTypes.func.isRequired,
  project: PropTypes.object,
};
